import { normaliseShopifyPrice } from '@utils/prices';
import { shopifyGraphqlIdToAdminId } from '@utils/shopifyIds';
import { useRouter } from 'next/router';
import { useCallback, useState } from 'react';

import { storefrontClient } from '../../../apollo/shopify/storefrontClient';
import { DEFAULT_CURRENCY_CODE } from '../../../constants/locations';
import { AML_LIMIT } from '../../../constants/product';
import { ShopifyCheckoutGateway } from '../../../gateways/ShopifyCheckoutGateway';
import { useCountryCode } from '../../../hooks/useCountryCode';
import { useAuth } from '../../../providers/AuthProvider';
import {
  useCalculateOrderPricingLazyQuery,
  useGenerateMultipassTokenMutation,
} from '../../../types/generated';
import { CurrencyCode } from '../../../types/Locations';
import { Frame } from '../../../types/Products';
import {
  AttributeInput,
  ShopifyProductPurchaseDetails,
  ShopifyProductToCheckout,
} from '../../../types/Purchasing';
import { captureError } from '../../../utils/capture';
import { useCheckoutAttributes } from './useCheckoutAttributes';

const checkoutGateway = new ShopifyCheckoutGateway(storefrontClient);

export type CheckoutHandoffItem = {
  frame: Frame | null;
  product: ShopifyProductPurchaseDetails;
  quantity: number;
};

export type PrivateRoomHandoff = {
  slug: string;
  unlockCodes: Record<string, string>;
};

type CheckoutHandoffArgs = {
  currency: CurrencyCode;
  items: CheckoutHandoffItem[];
  privateRoom?: PrivateRoomHandoff;
};

const unlockCodeFor = (
  privateRoom: PrivateRoomHandoff | undefined,
  product: ShopifyProductPurchaseDetails,
): string | undefined => {
  return privateRoom?.unlockCodes[
    shopifyGraphqlIdToAdminId(product.shopifyVariantId)
  ];
};

const frameSkuAttributes = (frame: Frame | null): AttributeInput[] => {
  return (frame?.variant.consolidatedVariants ?? []).flatMap(({ sku }) => {
    return sku ? [{ key: 'frame_sku', value: sku }] : [];
  });
};

const toProductsToCheckout = (
  items: CheckoutHandoffItem[],
  currency: CurrencyCode,
  privateRoom?: PrivateRoomHandoff,
): ShopifyProductToCheckout[] => {
  return items.flatMap(({ frame, product, quantity }) => {
    return [
      {
        customProperties: frameSkuAttributes(frame),
        product: {
          currency,
          hasResaleAgreement: !product.noResaleAgreement,
          noTaxCharged: Boolean(product.noTaxCharged),
          price: product.price,
          productId: product.shopifyId,
          productTitle: product.name,
          shopifyVariantId: product.shopifyVariantId,
        },
        quantity,
        unlockCode: unlockCodeFor(privateRoom, product),
      },
      ...(frame?.variant.consolidatedVariants ?? []).map((variant) => {
        return {
          product: {
            currency,
            hasResaleAgreement: false,
            noTaxCharged: false,
            price: variant.price,
            productId: variant.shopifyFrameId,
            productTitle: frame?.title ?? '',
            shopifyVariantId: variant.shopifyFrameVariantId,
          },
          quantity: variant.quantity,
        };
      }),
    ];
  });
};

const toPricingItems = (items: CheckoutHandoffItem[]) => {
  return items.flatMap(({ frame, product, quantity }) => {
    return [
      { quantity, variantId: product.shopifyVariantId },
      ...(frame?.variant.consolidatedVariants ?? []).map((variant) => {
        return {
          quantity: variant.quantity,
          variantId: variant.shopifyFrameVariantId,
        };
      }),
    ];
  });
};

export type CheckoutHandoff = {
  goToCheckout: (args: CheckoutHandoffArgs) => Promise<void>;
  loading: boolean;
};

export const useCheckoutHandoff = (): CheckoutHandoff => {
  const router = useRouter();
  const { isLoggedIn } = useAuth();
  const [countryCode] = useCountryCode();
  const [loading, setLoading] = useState(false);
  const [calculateOrderPricing] = useCalculateOrderPricingLazyQuery();
  const [generateMultipassToken] = useGenerateMultipassTokenMutation();

  const buildCustomAttributes = useCheckoutAttributes();

  const goToCheckout = useCallback(
    async ({ currency, items, privateRoom }: CheckoutHandoffArgs) => {
      const [single] = items;
      if (!single) return;

      setLoading(true);

      try {
        const customAttributes = await buildCustomAttributes({
          canBeFramed: items.some((item) => item.product.frames.length > 0),
          privateLinkSlug: privateRoom?.slug,
        });

        const checkoutUrl =
          items.length === 1
            ? await checkoutGateway.checkoutWithProduct({
                collectionSetProducts: [],
                currency,
                customAttributes,
                frame: single.frame,
                product: single.product,
                productAttributes: frameSkuAttributes(single.frame),
                quantity: single.quantity,
                unlockCode: unlockCodeFor(privateRoom, single.product),
              })
            : await checkoutGateway.checkoutWithListOfProducts(
                toProductsToCheckout(items, currency, privateRoom),
                currency,
                customAttributes,
              );

        let redirectUrl = checkoutUrl;
        if (isLoggedIn && !privateRoom) {
          const { data: multipass } = await generateMultipassToken({
            errorPolicy: 'all',
            variables: { returnTo: checkoutUrl },
          });
          const token = multipass?.generateMultipassToken.token;
          if (token) {
            redirectUrl = `${process.env.NEXT_PUBLIC_MULTIPASS_URL}${token}`;
          }
        }

        const { data } = await calculateOrderPricing({
          variables: {
            countryCode,
            currencyCode: DEFAULT_CURRENCY_CODE,
            items: toPricingItems(items),
          },
        });
        const totals = data?.calculateOrderPricing;

        if (
          totals?.totalPrice &&
          normaliseShopifyPrice(
            totals.totalPrice,
            totals.currencyCode as CurrencyCode,
          ) > AML_LIMIT
        ) {
          const searchParams = new URLSearchParams({ redirect: redirectUrl });
          await router.push({
            pathname: '/aml',
            search: `?${searchParams.toString()}`,
          });
          return;
        }

        await router.push(redirectUrl);
      } catch (error) {
        captureError(error);
      } finally {
        setLoading(false);
      }
    },
    [
      buildCustomAttributes,
      calculateOrderPricing,
      countryCode,
      generateMultipassToken,
      isLoggedIn,
      router,
    ],
  );

  return { goToCheckout, loading };
};
